import { useState } from "react";
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Typography,
  Stack, Chip, Divider, Paper, Tabs, Tab, Collapse, TextField, Tooltip, Alert,
  CircularProgress, useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import HistoryIcon from "@mui/icons-material/History";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { QRCodeSVG } from "qrcode.react";
import Swal from "sweetalert2";
import { fmtData as fmtDataSegura } from "../../utils/data";

const fmtData = (d) => fmtDataSegura(d);
const fmtDT = (d) => (d ? new Date(d).toLocaleString("pt-BR") : "—");
const moeda = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const SEM_CERTIFICADO = "Este evento não emite certificado.";

// Mesma regra da área administrativa: "Pago" pressupõe cobrança quitada.
const situacao = (m) =>
  m.Status === "X" ? { label: "Cancelada", cor: "default" }
  : Number(m.ValorTotal || 0) <= 0 ? { label: "Sem taxa", cor: "info" }
  : m.Pago ? { label: "Pago", cor: "success" }
  : m.Status === "C" ? { label: "Confirmada", cor: "success" }
  : { label: "Aguardando pagamento", cor: "warning" };

/** Área de pagamento: valor em destaque, QR sob demanda e aviso de validade. */
function AreaPagamento({ cob, onGerarPix, onAtualizar }) {
  const [aberto, setAberto] = useState(!cob.PixExpirado);
  const copiar = () => {
    navigator.clipboard?.writeText(cob.PixCopiaCola || "");
    Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Código PIX copiado", showConfirmButton: false, timer: 1500 });
  };

  return (
    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: "warning.50", border: "1px solid", borderColor: "warning.light" }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Box sx={{ flexGrow: 1, minWidth: 160 }}>
          <Typography variant="caption" color="text.secondary" display="block">Pagamento pendente</Typography>
          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{moeda(cob.ValorTotal)}</Typography>
        </Box>
        <Tooltip title="Verificar se o pagamento já foi identificado">
          <Button size="small" startIcon={<RefreshIcon />} onClick={onAtualizar}>Já paguei</Button>
        </Tooltip>
        {!cob.PixExpirado && cob.PixCopiaCola && (
          <Button size="small" variant={aberto ? "text" : "contained"} startIcon={<QrCode2Icon />} onClick={() => setAberto((a) => !a)}>
            {aberto ? "Ocultar PIX" : "Pagar com PIX"}
          </Button>
        )}
      </Stack>

      {cob.PixExpirado ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            O código PIX venceu{cob.PixExpiraEm ? ` em ${fmtDT(cob.PixExpiraEm)}` : ""}. Gere um novo para concluir o pagamento.
          </Typography>
          <Button size="small" variant="contained" startIcon={<RefreshIcon />} onClick={onGerarPix}>Gerar novo PIX</Button>
        </Box>
      ) : cob.PixCopiaCola ? (
        <Collapse in={aberto}>
          <Box sx={{ mt: 1.5, textAlign: "center" }}>
            <Box sx={{ display: "inline-block", p: 1.5, bgcolor: "#fff", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
              <QRCodeSVG value={cob.PixCopiaCola} size={172} level="M" />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              No app do banco: PIX → Ler QR Code
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <TextField fullWidth size="small" label="PIX copia e cola" value={cob.PixCopiaCola} InputProps={{ readOnly: true }} />
            <Tooltip title="Copiar"><IconButton onClick={copiar}><ContentCopyIcon /></IconButton></Tooltip>
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.8 }} flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">
              {cob.PixExpiraEm ? <>Pague até <b>{fmtDT(cob.PixExpiraEm)}</b></> : "O código pode vencer — gere outro se não conseguir pagar."}
            </Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={onGerarPix}>Gerar novo</Button>
          </Stack>
        </Collapse>
      ) : cob.PixErro ? (
        <Box sx={{ mt: 1 }}>
          <Alert severity="error" sx={{ mb: 1 }}>Não foi possível gerar o código PIX automaticamente.</Alert>
          <Button size="small" variant="contained" startIcon={<RefreshIcon />} onClick={onGerarPix}>Tentar de novo</Button>
        </Box>
      ) : (
        <Button size="small" sx={{ mt: 1 }} variant="contained" startIcon={<QrCode2Icon />} onClick={onGerarPix}>Gerar código PIX</Button>
      )}
    </Box>
  );
}

/** Um participante da inscrição (responsável ou aluno), com o certificado dele. */
function Participante({ item, onCertificado }) {
  const cert = item.Certificado;
  const mostraCert = cert && (cert.Liberado || cert.Motivo !== SEM_CERTIFICADO);
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.6 }}>
      <PersonIcon fontSize="small" sx={{ color: "text.disabled", mt: 0.3 }} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {item.AlunoNome || "Inscrição no seu nome"}
          {item.Turma && <Typography component="span" variant="caption" color="text.secondary"> — Turma {item.Turma}</Typography>}
        </Typography>
        {mostraCert && (cert.Liberado ? (
          <Button size="small" variant="outlined" sx={{ mt: 0.6 }} startIcon={<WorkspacePremiumIcon />}
            onClick={() => onCertificado(item.Id)}>
            Baixar certificado{cert.CargaHoraria ? ` • ${cert.CargaHoraria}h` : ""}
          </Button>
        ) : (
          <Typography variant="caption" color="text.disabled" display="block">Certificado: {cert.Motivo}</Typography>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap", mt: 0.3 }}>
        {item.ValorDevido > 0 ? moeda(item.ValorDevido) : "isento"}
      </Typography>
    </Stack>
  );
}

/** Cartão de uma inscrição feita neste portal. */
function CartaoInscricao({ m, onAtualizar, onGerarPix, onCertificado }) {
  const st = situacao(m);
  const presentes = (m.Encontros || []).filter((e) => e.Presente).length;
  const pendente = !m.Pago && m.Status === "A" && m.ValorTotal > 0;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 2 }}>
      <Stack direction={{ xs: "column-reverse", sm: "row" }} alignItems={{ xs: "flex-start", sm: "flex-start" }} spacing={1}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{m.EventoNome}</Typography>
          <Typography variant="caption" color="text.secondary">
            Inscrição feita em {fmtData(m.DtCadastro)}
            {m.Pago && m.DtPagamento ? ` • pago em ${fmtData(m.DtPagamento)}` : ""}
          </Typography>
        </Box>
        <Chip size="small" color={st.cor} label={st.label} sx={{ flexShrink: 0 }}
          icon={m.Pago ? <CheckCircleIcon /> : undefined}
          variant={m.Pago || m.Status === "C" ? "filled" : "outlined"} />
      </Stack>

      {m.Itens?.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          {m.Itens.map((it) => <Participante key={it.Id} item={it} onCertificado={onCertificado} />)}
        </>
      )}

      {m.Oficinas?.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>OFICINAS</Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.4 }}>
            {m.Oficinas.map((o, i) => (
              <Chip key={i} size="small" variant="outlined" label={o.Grupo ? `${o.Grupo}: ${o.Opcao}` : o.Opcao} />
            ))}
          </Stack>
        </Box>
      )}

      {m.Encontros?.length > 0 && (
        <Box sx={{ mt: 1.2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            FREQUÊNCIA — {presentes} de {m.Encontros.length}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.4 }}>
            {m.Encontros.map((en) => (
              <Tooltip key={en.Id} title={en.Presente && en.DtRegistro ? `Registrada em ${fmtDT(en.DtRegistro)}` : "Sem registro"}>
                <Chip size="small" color={en.Presente ? "success" : "default"} variant={en.Presente ? "filled" : "outlined"}
                  icon={en.Presente ? <CheckCircleIcon /> : undefined}
                  label={`${en.Nome}${en.Data ? ` (${fmtData(en.Data)})` : ""}`} />
              </Tooltip>
            ))}
          </Stack>
        </Box>
      )}

      {pendente ? (
        <AreaPagamento cob={m} onGerarPix={() => onGerarPix(m.Id)} onAtualizar={() => onAtualizar(m.Id)} />
      ) : m.ValorTotal > 0 && (
        <>
          <Divider sx={{ mt: 1.2, mb: 0.8 }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">Valor da inscrição</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{moeda(m.ValorTotal)}</Typography>
          </Stack>
        </>
      )}
    </Paper>
  );
}

/** Participação em evento do sistema anterior. */
function CartaoHistorico({ h, onCertificado }) {
  const periodo = h.DataInicio
    ? `${fmtData(h.DataInicio)}${h.DataFim && fmtData(h.DataFim) !== fmtData(h.DataInicio) ? ` a ${fmtData(h.DataFim)}` : ""}`
    : null;
  return (
    <Paper variant="outlined" sx={{ p: 1.8, mb: 1.2, borderRadius: 2 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ sm: "center" }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>{h.Evento}</Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.6 }}>
            {periodo && <Chip size="small" variant="outlined" label={periodo} />}
            {h.CargaHoraria > 0 && <Chip size="small" variant="outlined" label={`${h.CargaHoraria}h`} />}
            {h.Numero > 0 && <Chip size="small" variant="outlined" label={`nº ${h.Numero}`} />}
          </Stack>
          {!h.Liberado && (
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.6 }}>{h.Motivo}</Typography>
          )}
        </Box>
        {h.Liberado && (
          <Button size="small" variant="outlined" startIcon={<WorkspacePremiumIcon />}
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }} onClick={() => onCertificado(h.CodInscrito)}>
            Certificado
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

function Vazio({ icone: Icone, titulo, texto }) {
  return (
    <Box sx={{ textAlign: "center", py: 5, color: "text.secondary" }}>
      <Icone sx={{ fontSize: 44, color: "text.disabled", mb: 1 }} />
      <Typography variant="subtitle2">{titulo}</Typography>
      <Typography variant="caption">{texto}</Typography>
    </Box>
  );
}

/**
 * "Minhas inscrições": inscrições feitas no portal e participações anteriores
 * (sistema antigo), com pagamento, oficinas, frequência e certificados.
 */
export default function MinhasInscricoes({
  open, onClose, nome, minhas, historico,
  onAtualizar, onGerarPix, onCertificado, onCertificadoAntigo,
}) {
  const theme = useTheme();
  const telaPequena = useMediaQuery(theme.breakpoints.down("sm"));
  const [aba, setAba] = useState(0);

  const ativas = minhas.filter((m) => m.Status !== "X");
  const pendentes = ativas.filter((m) => !m.Pago && m.Status === "A" && m.ValorTotal > 0).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={telaPequena}
      PaperProps={{ sx: { borderRadius: telaPequena ? 0 : 3 } }}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack direction="row" alignItems="flex-start">
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>Minhas inscrições</Typography>
            {nome && <Typography variant="caption" color="text.secondary">{nome}</Typography>}
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Stack>
        <Tabs value={aba} onChange={(_, v) => setAba(v)} sx={{ mt: 1, minHeight: 42 }}>
          <Tab sx={{ minHeight: 42 }} label={
            <Stack direction="row" spacing={0.8} alignItems="center">
              <span>Inscrições ({minhas.length})</span>
              {pendentes > 0 && <Chip size="small" color="warning" label={`${pendentes} a pagar`} sx={{ height: 20 }} />}
            </Stack>} />
          <Tab sx={{ minHeight: 42 }} icon={<HistoryIcon fontSize="small" />} iconPosition="start"
            label={`Eventos anteriores${historico ? ` (${historico.length})` : ""}`} />
        </Tabs>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "grey.50" }}>
        {aba === 0 && (
          minhas.length === 0 ? (
            <Vazio icone={EventBusyIcon} titulo="Nenhuma inscrição por aqui"
              texto="Suas inscrições feitas neste portal aparecem nesta aba." />
          ) : (
            <>
              {minhas.map((m) => (
                <CartaoInscricao key={m.Id} m={m} onAtualizar={onAtualizar} onGerarPix={onGerarPix} onCertificado={onCertificado} />
              ))}
            </>
          )
        )}

        {aba === 1 && (
          historico === null ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center", py: 5 }}>
              <CircularProgress size={18} />
              <Typography variant="caption" color="text.secondary">Buscando participações anteriores…</Typography>
            </Box>
          ) : historico.length === 0 ? (
            <Vazio icone={HistoryIcon} titulo="Nenhum evento anterior"
              texto="Não encontramos participações no seu CPF antes deste portal." />
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.2 }}>
                Participações registradas no seu CPF antes deste portal. O certificado sai com o número de registro original.
              </Typography>
              {historico.map((h) => (
                <CartaoHistorico key={h.CodInscrito} h={h} onCertificado={onCertificadoAntigo} />
              ))}
            </>
          )
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
